import Info from './Glossary';
import type { ReactNode } from 'react';

/**
 * Shared surfaces for the dashboard.
 *
 * Everything used to be `border border-warm-dark-2 rounded-xl p-4`, so a headline
 * result and a secondary tool looked identical and the eye had nothing to rank.
 * Three levels instead: `primary` for the thing you came for, `default` for
 * supporting panels, `quiet` for reference material.
 */
export type CardTone = 'primary' | 'default' | 'quiet' | 'good' | 'warn';

const TONE: Record<CardTone, string> = {
  primary: 'border-cyan/30 bg-cyan/[0.04]',
  default: 'border-warm-dark-2 bg-warm-dark-2/20',
  quiet: 'border-warm-dark-2/60 bg-transparent',
  good: 'border-measured/40 bg-measured/5',
  warn: 'border-estimated/40 bg-estimated/5',
};

export function Card({ tone = 'default', className = '', children }: {
  tone?: CardTone; className?: string; children: ReactNode;
}): JSX.Element {
  return <div className={`border rounded-xl ${TONE[tone]} ${className || 'p-4'}`}>{children}</div>;
}

/** A card's header: title, optional one-line explanation, optional right-hand action. */
export function CardHead({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
      <div className="min-w-0">
        <h3 className="text-text-primary">{title}</h3>
        {hint && <p className="text-sm text-text-secondary mt-0.5">{hint}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/** Small uppercase rule above a group of cards, so long pages have structure. */
export function SectionLabel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="w-full flex items-center gap-3 mt-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-text-secondary/60 whitespace-nowrap">{children}</span>
      <span className="h-px flex-1 bg-warm-dark-2" />
    </div>
  );
}

/**
 * A KPI tile. The number is the chart: a single value with its scale, a label,
 * and — where we have one — an honest movement against the previous run.
 * `note` carries the caveat, because a number without its caveat is a claim.
 */
export function Stat({ label, value, unit, delta, deltaGoodWhen = 'up', tone, note, info }: {
  label: string;
  value: string | number;
  unit?: string;
  delta?: number | null;
  deltaGoodWhen?: 'up' | 'down';
  tone?: 'good' | 'warn' | 'bad' | 'neutral';
  note?: string;
  /** Glossary key — renders an ⓘ beside the label that explains the term. */
  info?: string;
}): JSX.Element {
  const valueColor = tone === 'good' ? 'text-success' : tone === 'warn' ? 'text-estimated'
    : tone === 'bad' ? 'text-[#E0574A]' : 'text-text-primary';
  const good = delta != null && delta !== 0 && (deltaGoodWhen === 'up' ? delta > 0 : delta < 0);
  return (
    <div className="border border-warm-dark-2 bg-warm-dark-2/20 rounded-xl px-4 py-3 min-w-0">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-secondary/70 truncate">{label}</span>
        {info && <Info term={info} />}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span className={`font-mono text-2xl leading-none ${valueColor}`}>{value}</span>
        {unit && <span className="font-mono text-sm text-text-secondary/60">{unit}</span>}
        {delta != null && delta !== 0 && (
          <span className={`font-mono text-xs ml-0.5 ${good ? 'text-success' : 'text-[#E0574A]'}`}>
            {delta > 0 ? '▲' : '▼'}{Math.abs(delta)}
          </span>
        )}
      </div>
      {note && <div className="text-sm text-text-secondary/80 mt-1 leading-snug">{note}</div>}
    </div>
  );
}

/**
 * A row of KPI tiles that reflows instead of squashing.
 *
 * Auto-fit rather than a fixed four columns: the row gained a fifth tile and a
 * hardcoded `lg:grid-cols-4` would have left it alone on a second line looking
 * like an afterthought. This fits as many as the width allows and keeps them
 * even.
 */
export function StatRow({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      className="grid grid-cols-2 gap-3 w-full"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))' }}
    >
      {children}
    </div>
  );
}
